import { useState, type KeyboardEvent } from 'react';
import { clampTo, parseTyped } from './terms-fields';

/**
 * A number the owner types or steps. Held as text while typed and committed on
 * leaving the field or Enter, so a half-typed "1" of "15" never reprices the card.
 */
export function useNumberDraft({
  value,
  min,
  max,
  step,
  onCommit,
}: {
  value: number;
  min: number;
  max: number;
  step: number;
  onCommit: (next: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const clamp = (n: number) => clampTo(n, min, max);

  const commit = () => {
    if (draft === null) return;
    const typed = parseTyped(draft);
    setDraft(null);
    if (typed !== null && clamp(typed) !== value) onCommit(clamp(typed));
  };

  return {
    text: draft ?? String(value),
    /** What is in the field right now, unclamped: null while it holds no number. */
    typed: draft === null ? value : parseTyped(draft),
    onChange: (text: string) => setDraft(text),
    onBlur: commit,
    onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') e.currentTarget.blur();
      // A typed value is put back first; only a second Escape reaches the editor around it.
      if (e.key === 'Escape' && draft !== null) {
        e.preventDefault();
        setDraft(null);
      }
    },
    less: () => onCommit(clamp(value - step)),
    more: () => onCommit(clamp(value + step)),
    atMin: value <= min,
    atMax: value >= max,
  };
}
